import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Alert from '@mui/material/Alert';
import type { AlertProps } from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { type ReactNode, type JSX } from 'react';

export type ContextInfoBannerSeverity = 'info' | 'warning';

export type ContextInfoBannerVariant = 'default' | 'sheet';

export type ContextInfoBannerProps = {
  message: string;
  severity?: ContextInfoBannerSeverity;
  variant?: ContextInfoBannerVariant;
  id?: string;
  action?: ReactNode;
  onDismiss?: () => void;
  closeButtonId?: string;
  className?: string;
  sx?: SxProps<Theme>;
};

export default function ContextInfoBanner({
  message,
  severity = 'info',
  variant = 'default',
  id,
  action,
  onDismiss,
  closeButtonId,
  className,
  sx,
}: ContextInfoBannerProps): JSX.Element {
  const alertAction =
    action || onDismiss ? (
      <>
        {action}
        {onDismiss ? (
          <IconButton
            id={closeButtonId}
            aria-label='Dismiss'
            size='small'
            onClick={onDismiss}
            // sx={{ ml: action ? 0.5 : 0 }}
          >
            <CloseRoundedIcon fontSize='small' />
          </IconButton>
        ) : null}
      </>
    ) : undefined;

  const alertProps: AlertProps = {
    severity,
    id,
    className,
    icon: false,
    action: alertAction,
    sx: [
      (theme) =>
        variant === 'sheet'
          ? {
              borderRadius: '12px 12px 0 0',
              mx: 0.25,
              mb: 0.5,
              borderBottom: 'none',
              boxShadow: theme.shadows[1],
              alignItems: 'flex-start',
              px: 1,
              pt: 0,
              pb: 1.5,
            }
          : {
              borderRadius: '12px',
              mx: 0.25,
              mb: 0.5,
              px: 1,
              py: 0,
              boxShadow: theme.shadows[1],
              alignItems: 'flex-start',
            },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ],
  };

  return (
    <Alert {...alertProps}>
      <Typography variant='captionRegular' component='span'>
        {message}
      </Typography>
    </Alert>
  );
}
